import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Check, Star, Crown, Sparkles, ArrowRight, Clock, 
  Users, Shield, Bell, X, Tag, Gift, Loader2, ArrowLeft, Rocket
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Apple IAP Product IDs
const TIER1_MONTHLY_ID = 'com.calmpathways.essential.monthly';
const TIER1_ANNUAL_ID = 'com.calmpathways.essential.yearly';
const TIER2_MONTHLY_ID = 'com.calmpathways.premium.monthly';
const TIER2_ANNUAL_ID = 'com.calmpathways.premium.yearly';

const PricingPage = () => {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [loading, setLoading] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistForm, setWaitlistForm] = useState({ name: '', email: '', notes: '' });
  const [waitlistCount, setWaitlistCount] = useState(0);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoApplied, setPromoApplied] = useState(null);
  const [showPromoInput, setShowPromoInput] = useState(false);
  
  // Launch promo state
  const [launchPromo, setLaunchPromo] = useState(null);

  useEffect(() => {
    fetchWaitlistCount();
    fetchLaunchPromoStatus();
  }, []);

  const getUserIdForIAP = () => {
    if (user?.id) return user.id;

    try {
      if (!token) return null;
      const payload = token.split('.')[1];
      if (!payload) return null;

      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const paddedBase64 = `${base64}${'='.repeat((4 - (base64.length % 4)) % 4)}`;
      const decoded = JSON.parse(atob(paddedBase64));
      return decoded?.sub || null;
    } catch (error) {
      return null;
    }
  };

  const isIOSWebView = () => {
    const userAgent = window.navigator.userAgent || '';
    const isIOSDevice = /iPhone|iPad|iPod/i.test(userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    return isIOSDevice && !!window?.webkit?.messageHandlers;
  };

  const postIAPPurchaseToNative = (planId) => {
    const iapHandler = window?.webkit?.messageHandlers?.iapHandler;
    if (!iapHandler?.postMessage) return false;

    // Map plan IDs to Apple product IDs
    let productId;
    if (planId === 'tier1_monthly') {
      productId = TIER1_MONTHLY_ID;
    } else if (planId === 'tier1_yearly') {
      productId = TIER1_ANNUAL_ID;
    } else if (planId === 'tier2_monthly') {
      productId = TIER2_MONTHLY_ID;
    } else if (planId === 'tier2_yearly') {
      productId = TIER2_ANNUAL_ID;
    } else {
      // Default fallback
      productId = planId?.includes('tier1') ? TIER1_MONTHLY_ID : TIER2_MONTHLY_ID;
    }

    const action = planId?.startsWith('tier1') ? 'choose_essential' : 'choose_premium';
    const userId = getUserIdForIAP();
    
    if (!userId) {
      sessionStorage.setItem('pendingIAP', JSON.stringify({ action, productId }));
      navigate('/auth?redirect=iap');
      return true;
    }

    iapHandler.postMessage({
      action,
      productId,
      packageId: productId,
      userId
    });

    return true;
  };

  const fetchLaunchPromoStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/launch-promo-status`);
      if (res.ok) {
        const data = await res.json();
        setLaunchPromo(data);
      }
    } catch (err) {
      console.error('Failed to fetch launch promo status');
    }
  };

  const fetchWaitlistCount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/tier3-waitlist/count`);
      if (res.ok) {
        const data = await res.json();
        setWaitlistCount(data.count);
      }
    } catch (err) {
      console.error('Failed to fetch waitlist count');
    }
  };

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) return;
    
    if (!user) {
      toast.error('Please sign in to use a promo code');
      navigate('/auth');
      return;
    }

    setPromoLoading(true);
    try {
      // First try to redeem (for 100% off codes)
      const redeemRes = await fetch(`${API_URL}/api/subscriptions/promo-codes/redeem?code=${promoCode}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const redeemData = await redeemRes.json();

      if (redeemRes.ok && redeemData.status === 'success') {
        // 100% discount code - subscription activated!
        toast.success(redeemData.message);
        setPromoApplied({
          code: promoCode.toUpperCase(),
          type: 'free',
          message: redeemData.message
        });
        if (refreshUser) await refreshUser();
        // Redirect to dashboard after short delay
        setTimeout(() => navigate('/dashboard'), 2000);
        return;
      } else if (redeemRes.ok && redeemData.status === 'discount') {
        // Partial discount - save for checkout
        setPromoApplied({
          code: promoCode.toUpperCase(),
          type: 'discount',
          discount_percent: redeemData.discount_percent,
          message: redeemData.message
        });
        toast.success(`${redeemData.discount_percent}% discount applied!`);
        return;
      }

      // If redeem failed, try validate for discount info
      const validateRes = await fetch(`${API_URL}/api/subscriptions/promo-codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoCode, plan_id: `tier2_${billingCycle}` })
      });

      if (validateRes.ok) {
        const validateData = await validateRes.json();
        setPromoApplied({
          code: promoCode.toUpperCase(),
          type: 'discount',
          discount_percent: validateData.discount_percent,
          final_price: validateData.final_price,
          original_price: validateData.original_price
        });
        toast.success(`${validateData.discount_percent}% discount will be applied!`);
      } else {
        const errorData = await validateRes.json();
        toast.error(errorData.detail || 'Invalid promo code');
        setPromoApplied(null);
      }
    } catch (err) {
      toast.error('Failed to apply promo code');
      setPromoApplied(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    // iOS WebView must use native IAP only (no Stripe).
    if (isIOSWebView()) {
      if (postIAPPurchaseToNative(planId)) {
        return;
      }

      toast.error('In-app purchase is unavailable. Please update the iOS app.');
      return;
    }

    if (!user) {
      // Store selected plan and redirect to signup
      sessionStorage.setItem('selectedPlan', planId);
      navigate('/auth?redirect=checkout');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin,
          promo_code: promoApplied?.code || null
        })
      });

      if (!res.ok) throw new Error('Failed to create checkout session');

      const data = await res.json();
      
      // Show promo message if applicable
      if (data.promo_applied && data.promo_message) {
        toast.success(data.promo_message);
      }
      
      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error('Payment error. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinWaitlist = async (e) => {
    e.preventDefault();
    
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/tier3-waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: waitlistForm.name,
          email: waitlistForm.email,
          interest_notes: waitlistForm.notes
        })
      });

      const data = await res.json();
      
      if (data.status === 'success') {
        toast.success(`You're #${data.position} on the waitlist!`);
        setShowWaitlistModal(false);
        setWaitlistForm({ name: '', email: '', notes: '' });
        fetchWaitlistCount();
      } else if (data.status === 'already_registered') {
        toast.info(data.message);
        setShowWaitlistModal(false);
      }
    } catch (err) {
      toast.error('Failed to join waitlist. Please try again.');
    }
  };

  const tiers = [
    {
      name: 'Essential',
      tier: 'tier1',
      icon: Star,
      color: 'primary',
      monthlyPrice: 9,
      yearlyPrice: 99,
      description: 'Complete tracking for health management',
      features: [
        'Personalized Dashboard',
        'Complete Knowledge Base',
        'Community Forum Access',
        'Symptom & Trigger Tracker',
        'Voice Journaling with AI',
        'Weather Tracking & Correlations',
        'Cycle/Period Tracker',
        'Sleep Tracker',
        'Food Tracker',
        'Flare Journal',
        'Parent & Child Regulation Tools',
        'Coping Library',
        'AI Health Chat (Limited)'
      ],
      cta: 'Get Started'
    },
    {
      name: 'Premium',
      tier: 'tier2',
      icon: Crown,
      color: 'secondary',
      monthlyPrice: 19,
      yearlyPrice: 199,
      description: 'Complete toolkit for optimal health outcomes',
      popular: true,
      features: [
        'Everything in Essential',
        'Unlimited AI Health Chat',
        'Smart Correlations & Trigger Detection',
        'Symptom Photo Log with AI Analysis',
        'Flare Predictions & Warnings',
        'Weekly Health Reports (Coming Soon)',
        'Meal Planning Tools',
        'Lab Results Tracking',
        'Med & Supplement Tracker',
        'Appointment Tracker',
        'Medical Timeline View',
        'PDF Export & Reports'
      ],
      cta: 'Go Premium'
    },
    {
      name: 'Professional',
      tier: 'tier3',
      icon: Sparkles,
      color: 'accent',
      monthlyPrice: 200,
      yearlyPrice: 2000,
      description: 'Premium support with direct specialist access',
      comingSoon: true,
      limited: true,
      features: [
        'Everything in Premium',
        'Async Messaging with Specialist',
        'Lab Interpretation (Educational)',
        'Personalized Educational Summaries',
        'Care Pathway Education',
        'Priority Access to New Tools',
        'Direct Support Channel'
      ],
      cta: 'Join Waitlist'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F0E8] to-white">
      {/* Header */}
      <div className="text-center pt-12 pb-8 px-4">
        {/* Back Button */}
        <div className="max-w-6xl mx-auto mb-6 text-left">
          <Button 
            onClick={() => navigate(-1)} 
            variant="ghost" 
            className="text-primary hover:text-primary/80"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
        </div>

        <h1 className="text-4xl md:text-5xl font-nunito font-bold text-[#2D2D2D] mb-4">
          Choose Your Path
        </h1>
        <p className="text-lg text-[#57534E] max-w-2xl mx-auto mb-8">
          Join thousands of families navigating the PANDAS/PANS journey with confidence. 
          Every plan includes our comprehensive knowledge base and supportive community.
        </p>

        {/* Billing Toggle */}
        <div className="inline-flex items-center gap-3 bg-white rounded-full p-1.5 shadow-sm border border-stone-200">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
              billingCycle === 'monthly'
                ? 'bg-primary text-white'
                : 'text-[#57534E] hover:text-[#2D2D2D]'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
              billingCycle === 'yearly'
                ? 'bg-primary text-white'
                : 'text-[#57534E] hover:text-[#2D2D2D]'
            }`}
          >
            Yearly
            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
              Save up to 17%
            </span>
          </button>
        </div>

        {/* Promo Code Section */}
        <div className="mt-6">
          {!showPromoInput && !promoApplied ? (
            <button
              onClick={() => setShowPromoInput(true)}
              className="text-sm text-primary hover:text-primary/80 flex items-center gap-1 mx-auto"
              data-testid="show-promo-input"
            >
              <Tag className="w-4 h-4" />
              Have a promo code?
            </button>
          ) : promoApplied ? (
            <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-2">
              <Gift className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700 font-medium">
                {promoApplied.type === 'free' 
                  ? `Code "${promoApplied.code}" applied - Subscription activated!`
                  : `Code "${promoApplied.code}" applied - ${promoApplied.discount_percent}% off!`
                }
              </span>
              <button 
                onClick={() => { setPromoApplied(null); setPromoCode(''); }}
                className="text-green-600 hover:text-green-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 bg-white rounded-full p-1 shadow-sm border border-stone-200">
              <Input
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="Enter code"
                className="border-0 shadow-none focus-visible:ring-0 w-32 font-mono text-center"
                data-testid="promo-code-input"
              />
              <Button
                onClick={handleApplyPromoCode}
                disabled={promoLoading || !promoCode.trim()}
                size="sm"
                className="rounded-full bg-primary"
              >
                {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
              </Button>
              <button
                onClick={() => { setShowPromoInput(false); setPromoCode(''); }}
                className="text-stone-400 hover:text-stone-600 pr-2"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-4 pb-16">
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {tiers.map((tier) => {
            const Icon = tier.icon;
            const price = billingCycle === 'yearly' ? tier.yearlyPrice : tier.monthlyPrice;
            const planId = `${tier.tier}_${billingCycle}`;

            return (
              <Card
                key={tier.tier}
                className={`relative rounded-2xl p-6 lg:p-8 ${
                  tier.popular
                    ? 'border-2 border-secondary shadow-xl scale-105 z-10'
                    : tier.comingSoon
                    ? 'border border-stone-200 bg-stone-50/50'
                    : 'border border-stone-200'
                }`}
                data-testid={`pricing-card-${tier.tier}`}
              >
                {/* Popular Badge */}
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-secondary text-white text-sm font-medium px-4 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                {/* Coming Soon Badge */}
                {tier.comingSoon && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-accent text-white text-sm font-medium px-4 py-1 rounded-full flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Coming Soon
                    </span>
                  </div>
                )}

                {/* Limited Badge */}
                {tier.limited && (
                  <div className="absolute top-4 right-4">
                    <span className="bg-amber-100 text-amber-800 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      Limited Spots
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full bg-${tier.color}/10 mb-4`}>
                    <Icon className={`w-6 h-6 text-${tier.color}`} />
                  </div>
                  <h2 className="text-2xl font-nunito font-bold text-[#2D2D2D]">{tier.name}</h2>
                  <p className="text-sm text-[#57534E] mt-1">{tier.description}</p>
                </div>

                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-[#2D2D2D]">${price}</span>
                    <span className="text-[#57534E]">/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
                  </div>
                  {billingCycle === 'yearly' && (
                    <p className="text-sm text-green-600 mt-1">
                      Save ${(tier.monthlyPrice * 12) - tier.yearlyPrice}/year
                    </p>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className={`w-5 h-5 text-${tier.color} flex-shrink-0 mt-0.5`} />
                      <span className="text-sm text-[#57534E]">{feature}</span>
                    </li>
                  ))}
                </ul>

                {tier.comingSoon ? (
                  <Button
                    onClick={() => setShowWaitlistModal(true)}
                    className="w-full bg-accent hover:bg-accent/90 text-white"
                    data-testid="join-waitlist-btn"
                  >
                    <Bell className="w-4 h-4 mr-2" />
                    {tier.cta}
                    {waitlistCount > 0 && (
                      <span className="ml-2 text-xs opacity-75">({waitlistCount} joined)</span>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleSubscribe(planId)}
                    disabled={loading}
                    className={`w-full ${
                      tier.popular
                        ? 'bg-secondary hover:bg-secondary/90 text-white'
                        : 'bg-primary hover:bg-primary/90 text-white'
                    }`}
                    data-testid={`subscribe-btn-${tier.tier}`}
                  >
                    {loading ? 'Processing...' : tier.cta}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </Card>
            );
          })}
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 text-center">
          <div className="flex flex-wrap justify-center gap-8 items-center text-[#57534E]">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              <span className="text-sm">Secure Payments via Stripe</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              <span className="text-sm">Trusted by PANDAS/PANS Families</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              <span className="text-sm">Cancel Anytime</span>
            </div>
          </div>
        </div>

        {/* Future Add-ons */}
        <div className="mt-16 max-w-3xl mx-auto">
          <Card className="bg-stone-50 border-stone-200 rounded-2xl p-8">
            <h3 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-4 text-center">
              Coming Soon: Additional Services
            </h3>
            <div className="grid md:grid-cols-3 gap-4 text-center">
              <div className="p-4">
                <p className="font-semibold text-[#2D2D2D]">Lab Testing Packages</p>
                <p className="text-sm text-[#57534E]">$150 - $1,200+</p>
              </div>
              <div className="p-4">
                <p className="font-semibold text-[#2D2D2D]">One-Time Consult Review</p>
                <p className="text-sm text-[#57534E]">$150 - $300</p>
              </div>
              <div className="p-4">
                <p className="font-semibold text-[#2D2D2D]">Extended Written Summaries</p>
                <p className="text-sm text-[#57534E]">$75 - $150</p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Waitlist Modal */}
      {showWaitlistModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="bg-white rounded-2xl p-6 max-w-md w-full relative">
            <button
              onClick={() => setShowWaitlistModal(false)}
              className="absolute top-4 right-4 text-stone-400 hover:text-stone-600"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent/10 mb-4">
                <Sparkles className="w-6 h-6 text-accent" />
              </div>
              <h2 className="text-2xl font-nunito font-bold text-[#2D2D2D]">Join the Pro Waitlist</h2>
              <p className="text-sm text-[#57534E] mt-2">
                Be first in line when we launch specialist support. Limited spots available to ensure quality care.
              </p>
            </div>

            <form onSubmit={handleJoinWaitlist} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#2D2D2D]">Your Name</label>
                <Input
                  value={waitlistForm.name}
                  onChange={(e) => setWaitlistForm({ ...waitlistForm, name: e.target.value })}
                  placeholder="Enter your name"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2D2D2D]">Email Address</label>
                <Input
                  type="email"
                  value={waitlistForm.email}
                  onChange={(e) => setWaitlistForm({ ...waitlistForm, email: e.target.value })}
                  placeholder="Enter your email"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2D2D2D]">What interests you most? (optional)</label>
                <Textarea
                  value={waitlistForm.notes}
                  onChange={(e) => setWaitlistForm({ ...waitlistForm, notes: e.target.value })}
                  placeholder="Tell us about your needs..."
                  className="mt-1"
                  rows={3}
                />
              </div>
              <Button type="submit" className="w-full bg-accent hover:bg-accent/90 text-white">
                <Bell className="w-4 h-4 mr-2" />
                Join Waitlist
              </Button>
            </form>

            {waitlistCount > 0 && (
              <p className="text-center text-sm text-[#57534E] mt-4">
                {waitlistCount} families already on the waitlist
              </p>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
